import React, { useEffect, useState } from "react";
import { Save, Trash2, BarChart3, FileText, Settings2, Users } from "lucide-react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";

const input = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orange/40";
const Field = ({ label, value, onChange, area, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-navy mb-1">{label}</label>
    {area ? <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} />
      : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={input} />}
    {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
  </div>
);
const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm space-y-4">
    <h2 className="font-display font-bold text-navy text-sm">{title}</h2>
    {children}
  </div>
);

export default function CalculatorManager() {
  const [tab, setTab] = useState("content");
  const [cfg, setCfg] = useState(null);
  const [guides, setGuides] = useState([]);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState([]);
  const [funnel, setFunnel] = useState({});

  useEffect(() => {
    adminApi.calculator.getConfig().then(setCfg).catch(() => toast.error("Could not load config"));
    adminApi.guides.list().then(setGuides).catch(() => setGuides([]));
    adminApi.calculator.submissions().then(setSubs).catch(() => setSubs([]));
    adminApi.calculator.funnel().then(setFunnel).catch(() => setFunnel({}));
  }, []);

  if (!cfg) return <p className="text-slate-400">Loading…</p>;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setMetric = (key, field, v) => setCfg((c) => ({ ...c, metrics: { ...c.metrics, [key]: { ...c.metrics[key], [field]: v } } }));
  const setAttention = (key, v) => setCfg((c) => ({ ...c, attention: { ...c.attention, [key]: v } }));

  const save = async () => {
    setSaving(true);
    try { await adminApi.calculator.saveConfig(cfg); toast.success("Calculator saved"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const removeSub = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    await adminApi.calculator.removeSubmission(id);
    setSubs((s) => s.filter((x) => x.id !== id));
  };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`calc-admin-tab-${id}`}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === id ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-orange/40"}`}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display font-extrabold text-2xl text-navy">Business Numbers Calculator</h1>
        {tab !== "submissions" && tab !== "analytics" && (
          <button onClick={save} disabled={saving} data-testid="calc-admin-save" className="inline-flex items-center gap-1.5 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors">
            <Save size={15} /> {saving ? "Saving…" : "Save"}
          </button>
        )}
      </div>
      <p className="text-slate-500 text-sm mb-5">Edit all wording, choose which metrics show, and review leads. Formulas are fixed in code and can't be broken here.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        <TabBtn id="content" icon={FileText} label="Content & CTAs" />
        <TabBtn id="metrics" icon={Settings2} label="Metrics" />
        <TabBtn id="submissions" icon={Users} label={`Submissions (${subs.length})`} />
        <TabBtn id="analytics" icon={BarChart3} label="Analytics" />
      </div>

      {tab === "content" && (
        <div className="max-w-2xl space-y-5">
          <Card title="Headline & intro">
            <Field label="Title" value={cfg.title} onChange={(v) => set("title", v)} />
            <Field label="Subtitle" value={cfg.subtitle} onChange={(v) => set("subtitle", v)} />
            <Field label="Intro" value={cfg.intro} onChange={(v) => set("intro", v)} area />
            <Field label="Workbook note" value={cfg.workbook_note} onChange={(v) => set("workbook_note", v)} area />
          </Card>
          <Card title="Hub tile image">
            <p className="text-[11px] text-slate-400 -mt-1">Optional. Shown on the calculator's tile on the Business Assessment Tools hub (in place of the icon). Landscape images work best.</p>
            <ImageUpload label="Tile image" value={cfg.hub_tile_image} onChange={(v) => set("hub_tile_image", v)} />
            <Field label="Image alt text" value={cfg.hub_tile_image_alt} onChange={(v) => set("hub_tile_image_alt", v)} />
          </Card>
          <Card title="Call to action">
            <Field label="CTA heading" value={cfg.cta_heading} onChange={(v) => set("cta_heading", v)} />
            <Field label="CTA body" value={cfg.cta_body} onChange={(v) => set("cta_body", v)} area />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primary button label" value={cfg.cta_primary_label} onChange={(v) => set("cta_primary_label", v)} />
              <Field label="Primary button link" value={cfg.cta_primary_link} onChange={(v) => set("cta_primary_link", v)} />
            </div>
            <Field label="Secondary (workbook) button label" value={cfg.cta_secondary_label} onChange={(v) => set("cta_secondary_label", v)} />
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Workbook 2 guide</label>
              <select value={cfg.workbook_guide_id || ""} onChange={(e) => set("workbook_guide_id", e.target.value)} className={input} data-testid="calc-admin-workbook">
                <option value="">— Auto-match a guide titled "10 Numbers" —</option>
                {guides.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </select>
            </div>
          </Card>
          <Card title="Personalised summary">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!cfg.ai_summary_enabled} onChange={(e) => set("ai_summary_enabled", e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange" data-testid="calc-admin-ai-toggle" />
              <span>
                <span className="text-sm font-semibold text-navy">Use AI-written summary</span>
                <span className="block text-[11px] text-slate-400 mt-0.5">When on, the "What your numbers are telling you" summary is written fresh by AI (Claude) from each user's results. When off, the built-in rule-based summary is used. Uses your Emergent LLM key.</span>
              </span>
            </label>
          </Card>
          <Card title="Follow-up nurture email">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!cfg.followup_enabled} onChange={(e) => set("followup_enabled", e.target.checked)} className="mt-0.5 w-4 h-4 accent-orange" data-testid="calc-admin-followup-toggle" />
              <span>
                <span className="text-sm font-semibold text-navy">Send a follow-up email to calculator leads</span>
                <span className="block text-[11px] text-slate-400 mt-0.5">A short email is sent automatically a few days after someone requests their report, offering a free numbers review. Each lead is emailed only once.</span>
              </span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-navy mb-1">Send after (days)</label>
                <input type="number" value={cfg.followup_days ?? 3} onChange={(e) => set("followup_days", parseInt(e.target.value) || 0)} className={input} data-testid="calc-admin-followup-days" />
              </div>
              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                <input type="checkbox" checked={!!cfg.followup_marketing_only} onChange={(e) => set("followup_marketing_only", e.target.checked)} className="w-4 h-4 accent-orange" />
                <span className="text-xs font-semibold text-slate-600">Only email marketing opt-ins</span>
              </label>
            </div>
            <Field label="Subject" value={cfg.followup_subject} onChange={(v) => set("followup_subject", v)} />
            <Field label="Body (use {name}; blank line = new paragraph)" value={cfg.followup_body} onChange={(v) => set("followup_body", v)} area />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Button label" value={cfg.followup_cta_label} onChange={(v) => set("followup_cta_label", v)} />
              <Field label="Button link" value={cfg.followup_cta_link} onChange={(v) => set("followup_cta_link", v)} />
            </div>
          </Card>
          <Card title="Headings, lead form & disclaimer">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Summary heading" value={cfg.summary_heading} onChange={(v) => set("summary_heading", v)} />
              <Field label="Attention heading" value={cfg.attention_heading} onChange={(v) => set("attention_heading", v)} />
              <Field label="What-if heading" value={cfg.whatif_heading} onChange={(v) => set("whatif_heading", v)} />
              <Field label="Action plan heading" value={cfg.action_heading} onChange={(v) => set("action_heading", v)} />
            </div>
            <Field label="Lead form heading" value={cfg.lead_heading} onChange={(v) => set("lead_heading", v)} />
            <Field label="Lead form body" value={cfg.lead_body} onChange={(v) => set("lead_body", v)} area />
            <Field label="Disclaimer" value={cfg.disclaimer} onChange={(v) => set("disclaimer", v)} area />
          </Card>
          <Card title="Three-numbers attention wording">
            {Object.keys(cfg.attention || {}).map((k) => (
              <Field key={k} label={k} value={cfg.attention[k]} onChange={(v) => setAttention(k, v)} area />
            ))}
          </Card>
        </div>
      )}

      {tab === "metrics" && (
        <div className="max-w-3xl space-y-3">
          {Object.keys(cfg.metrics || {}).map((key) => {
            const m = cfg.metrics[key];
            return (
              <div key={key} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm" data-testid={`calc-admin-metric-${key}`}>
                <div className="flex items-center gap-3 mb-2">
                  <input value={m.name || ""} onChange={(e) => setMetric(key, "name", e.target.value)} className="font-semibold text-navy text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 flex-1" />
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 cursor-pointer whitespace-nowrap">
                    <input type="checkbox" checked={m.visible !== false} onChange={(e) => setMetric(key, "visible", e.target.checked)} className="w-4 h-4 accent-orange" data-testid={`calc-admin-visible-${key}`} /> Visible
                  </label>
                </div>
                <div className="grid gap-2">
                  <Field label="What it means (use {value})" value={m.means} onChange={(v) => setMetric(key, "means", v)} area />
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Why it matters" value={m.matters} onChange={(v) => setMetric(key, "matters", v)} area />
                    <Field label="What to consider" value={m.consider} onChange={(v) => setMetric(key, "consider", v)} area />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "submissions" && (
        <div className="space-y-3">
          {subs.length === 0 ? <p className="text-sm text-slate-400">No submissions yet.</p> : subs.map((s) => (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center gap-4" data-testid={`calc-sub-${s.id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate">{s.name || "—"} {s.business_name ? <span className="text-slate-400 font-normal">· {s.business_name}</span> : null}</p>
                <p className="text-xs text-slate-400 truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""} · {new Date(s.created_at).toLocaleDateString("en-GB")} {s.marketing_consent ? <span className="text-green-600 font-semibold">· opted in</span> : ""}</p>
              </div>
              <button onClick={() => removeSub(s.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      {tab === "analytics" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-3xl">
          {[["start", "Started"], ["email_capture_open", "Opened report form"], ["email_capture", "Reports generated"], ["report_download", "PDF downloads"], ["cta_click", "\"Let's Talk\" clicks"], ["workbook_download", "Workbook clicks"]].map(([k, label]) => (
            <div key={k} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid={`calc-funnel-${k}`}>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
              <p className="font-display font-extrabold text-3xl text-navy mt-1">{funnel[k] || 0}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
